import { ref, update, get } from 'firebase/database';
import { database } from '../firebaseConfig';
import { DateTime } from 'luxon';

class AutoLockService {
  constructor() {
    this.checkInterval = null;
    this.isRunning = false;
  }

  start() {
    if (this.isRunning) return;
    
    this.isRunning = true;
    console.log('AutoLockService: Starting automatic game locking service');
    
    // Check every minute for games that need to be locked
    this.checkInterval = setInterval(() => {
      this.checkAndLockGames();
    }, 60000); // Check every minute
    
    // Also check immediately when starting
    this.checkAndLockGames();
  }

  stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.isRunning = false;
    console.log('AutoLockService: Stopped automatic game locking service');
  }

  async checkAndLockGames() {
    try {
      const gamesRef = ref(database, 'games');
      const gamesSnapshot = await get(gamesRef);
      const gamesData = gamesSnapshot.val();
      
      if (!gamesData) return;
      
      // Use UTC time for consistent locking across all timezones
      const now = DateTime.utc();
      const updates = {};
      let hasUpdates = false;
      
      Object.keys(gamesData).forEach((gameId) => {
        const game = gamesData[gameId];
        
        // Skip if game is already locked, doesn't have a dateTime, or is not active
        if (game.locked || !game.dateTime || !game.active) return;
        
        // Parse the game start time (stored in UTC)
        const gameStartTime = DateTime.fromISO(game.dateTime);
        const lockTime = gameStartTime.minus({ minutes: 5 }); // Lock 5 minutes before
        
        // If it's time to lock the game (comparing UTC times)
        if (now >= lockTime) {
          updates[`games/${gameId}/locked`] = true;
          hasUpdates = true;
          console.log(`AutoLockService: Locking game ${gameId} (${game.awayTeam} @ ${game.homeTeam}) - Start time: ${gameStartTime.toFormat('yyyy-MM-dd HH:mm')} UTC`);
        }
      });
      
      // Apply all updates at once if there are any
      if (hasUpdates) {
        await update(ref(database), updates);
        console.log(`AutoLockService: Locked ${Object.keys(updates).length / 2} games`);
        
        // Dispatch a custom event to notify components about the lock
        window.dispatchEvent(new CustomEvent('gamesLocked', { 
          detail: { 
            count: Object.keys(updates).length / 2,
            timestamp: now.toISO()
          }
        }));
      }
      
    } catch (error) {
      console.error('AutoLockService: Error checking and locking games:', error);
    }
  }

  formatTimeUntilLock(lockTime, now) {
    if (now >= lockTime) {
      return 'LOCKED';
    }

    const totalMinutes = Math.max(0, Math.floor(lockTime.diff(now).as('minutes')));
    if (totalMinutes <= 0) {
      return 'Locking soon...';
    }

    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;
    const unit = (value, label) => `${value} ${value === 1 ? label : `${label}s`}`;
    const parts = [];

    if (days > 0) parts.push(unit(days, 'day'));
    if (hours > 0) parts.push(unit(hours, 'hour'));
    if (minutes > 0 && days === 0) parts.push(unit(minutes, 'minute'));

    return `${parts.join(' ')} until lock`;
  }

  // Get time until lock for a specific game (for display purposes)
  getTimeUntilLock(dateTime) {
    if (!dateTime) return null;

    const lockTime = DateTime.fromISO(dateTime).minus({ minutes: 5 });
    return this.formatTimeUntilLock(lockTime, DateTime.utc());
  }

  // Check if a game is currently locked or will be locked soon
  getGameLockStatus(dateTime) {
    if (!dateTime) return { isLocked: false, timeUntilLock: null };

    const lockTime = DateTime.fromISO(dateTime).minus({ minutes: 5 });
    const now = DateTime.utc();
    const timeUntilLock = this.formatTimeUntilLock(lockTime, now);

    return {
      isLocked: timeUntilLock === 'LOCKED',
      timeUntilLock
    };
  }
}

// Create a singleton instance
const autoLockService = new AutoLockService();

export default autoLockService;
